import { html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { type StyleInfo, styleMap } from "lit/directives/style-map.js";

import AtlasElement, { type AtlasElementProps } from "@/components/atlas-element";
import styles from "./atlas-page-section.scss";
import { Theme, ThemeVariation } from "@/internals/theme";

export type PageSectionProps = AtlasElementProps & {
    "container"?: boolean;
    "white"?: boolean;
    "theme"?: Theme;
    "theme-variation"?: ThemeVariation;
    "background-image"?: string;
    "vertical-padding": "none" | "sm" | "md" | "lg";
};

/**
 * O componente pode ser utilizado para criar seções de página, com ou sem fundo branco e com ou sem o comportamento de container.
 *
 * @tag atlas-page-section
 */
@customElement("atlas-page-section")
export default class AtlasPageSection extends AtlasElement {
    static styles = styles;

    /** Indica se a página vai ter o comportamento de um container (Centralizando o conteúdo na página) */
    @property({ type: Boolean, reflect: true }) container: boolean = false;

    /** Indica se a seção vai ter o fundo branco */
    @property({ type: Boolean, reflect: true }) white: boolean = false;

    /** Cor de fundo da seção */
    @property({ type: String }) theme: Theme;

    /** A variação da tonalidade da cor do tema de fundo da seção*/
    @property({ type: Number, attribute: "theme-variation" }) themeVariation: ThemeVariation;

    /** Caminho da imagem que aparece como fundo da seção */
    @property({ type: String, attribute: "background-image" }) backgroundImage: string;

    /** Espaçamento interno vertical da seção */
    @property({ type: String, attribute: "vertical-padding" }) verticalPadding: "none" | "sm" | "md" | "lg" = "md";

    private getThemeClass() {
        if (this.theme) {
            if (this.themeVariation) {
                return `bg-${this.theme}-${this.themeVariation}`;
            }

            return `bg-${this.theme}`;
        }

        return "";
    }

    render() {
        const themeClass = this.getThemeClass();

        const pageSectionClasses = {
            "page-section": true,
            "white": this.white,
            "with-background": !!this.backgroundImage,
            [`padding-${this.verticalPadding}`]: !!this.verticalPadding,
            [themeClass]: !!themeClass
        };

        const containerClasses = {
            container: this.container
        };

        const pageSectionStyles: StyleInfo = this.backgroundImage
            ? { backgroundImage: `url("${this.backgroundImage}")` }
            : {};

        return html`
            <div class="${classMap(pageSectionClasses)}" style=${styleMap(pageSectionStyles)}>
                <div class="${classMap(containerClasses)}">
                    <slot></slot>
                </div>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-page-section": AtlasPageSection;
    }
}
